// CB:BEGIN admin-priority2-contractor-profile-editor-component
/**
 * Contractor Profile Editor Component
 * Allows admins to directly edit contractor profiles, licenses, insurance, and service states
 */
import React, { useState } from 'react';
import { useApi } from '../../lib/useApi';

interface ContractorProfileEditorProps {
  contractorId: string;
  contractor: {
    company?: string | null;
  };
  verification?: {
    license_number?: string | null;
    licenseState?: string | null;
    licenseExpiresAt?: string | null;
    generalLiabilityExpiresAt?: string | null;
    workersCompExpiresAt?: string | null;
    workersCompRequired?: boolean | null;
    serviceStates?: string[];
  } | null;
  onSuccess: () => void;
}

export const ContractorProfileEditor: React.FC<ContractorProfileEditorProps> = ({
  contractorId,
  contractor,
  verification,
  onSuccess
}) => {
  const api = useApi();
  const [loading, setLoading] = useState(false);
  const [showProfileModal, setShowProfileModal] = useState(false);
  const [showLicenseModal, setShowLicenseModal] = useState(false);
  const [showInsuranceModal, setShowInsuranceModal] = useState(false);
  const [showServiceStatesModal, setShowServiceStatesModal] = useState(false);

  // Profile editing
  const [company, setCompany] = useState(contractor.company || '');
  const [profileReason, setProfileReason] = useState('');

  // License editing
  const [licenseNumber, setLicenseNumber] = useState(verification?.license_number || '');
  const [licenseState, setLicenseState] = useState(verification?.licenseState || '');
  const [licenseExpiresAt, setLicenseExpiresAt] = useState(
    verification?.licenseExpiresAt ? new Date(verification.licenseExpiresAt).toISOString().split('T')[0] : ''
  );
  const [licenseReason, setLicenseReason] = useState('');

  // Insurance editing
  const [generalLiabilityExpiresAt, setGeneralLiabilityExpiresAt] = useState(
    verification?.generalLiabilityExpiresAt ? new Date(verification.generalLiabilityExpiresAt).toISOString().split('T')[0] : ''
  );
  const [workersCompExpiresAt, setWorkersCompExpiresAt] = useState(
    verification?.workersCompExpiresAt ? new Date(verification.workersCompExpiresAt).toISOString().split('T')[0] : ''
  );
  const [workersCompRequired, setWorkersCompRequired] = useState(verification?.workersCompRequired ?? false);
  const [insuranceReason, setInsuranceReason] = useState('');

  // Service states editing
  const [serviceStatesText, setServiceStatesText] = useState((verification?.serviceStates || []).join(', '));
  const [serviceStatesReason, setServiceStatesReason] = useState('');

  const handleProfileEdit = async () => {
    if (!company.trim()) {
      alert('Company name required');
      return;
    }

    if (!profileReason.trim()) {
      alert('Edit reason required');
      return;
    }

    setLoading(true);
    try {
      await api.patch(`/api/admin/contractors/${contractorId}/profile`, {
        company: company.trim(),
        reason: profileReason
      });
      alert('✅ Contractor profile updated');
      setShowProfileModal(false);
      setProfileReason('');
      onSuccess();
    } catch (e: any) {
      alert(`❌ Error: ${e.message || 'Failed to update profile'}`);
    } finally {
      setLoading(false);
    }
  };

  const handleLicenseEdit = async () => {
    if (!licenseReason.trim()) {
      alert('Edit reason required');
      return;
    }

    setLoading(true);
    try {
      await api.patch(`/api/admin/contractors/${contractorId}/license`, {
        licenseNumber: licenseNumber.trim() || null,
        licenseState: licenseState.trim() || null,
        licenseExpiresAt: licenseExpiresAt || null,
        reason: licenseReason
      });
      alert('✅ License information updated');
      setShowLicenseModal(false);
      setLicenseReason('');
      onSuccess();
    } catch (e: any) {
      alert(`❌ Error: ${e.message || 'Failed to update license'}`);
    } finally {
      setLoading(false);
    }
  };

  const handleInsuranceEdit = async () => {
    if (!insuranceReason.trim()) {
      alert('Edit reason required');
      return;
    }

    setLoading(true);
    try {
      await api.patch(`/api/admin/contractors/${contractorId}/insurance`, {
        generalLiabilityExpiresAt: generalLiabilityExpiresAt || null,
        workersCompExpiresAt: workersCompExpiresAt || null,
        workersCompRequired,
        reason: insuranceReason
      });
      alert('✅ Insurance information updated');
      setShowInsuranceModal(false);
      setInsuranceReason('');
      onSuccess();
    } catch (e: any) {
      alert(`❌ Error: ${e.message || 'Failed to update insurance'}`);
    } finally {
      setLoading(false);
    }
  };

  const handleServiceStatesEdit = async () => {
    if (!serviceStatesReason.trim()) {
      alert('Edit reason required');
      return;
    }

    // Parse comma-separated states
    const serviceStates = serviceStatesText
      .split(',')
      .map(s => s.trim().toUpperCase())
      .filter(Boolean);

    setLoading(true);
    try {
      await api.patch(`/api/admin/contractors/${contractorId}/service-states`, {
        serviceStates,
        reason: serviceStatesReason
      });
      alert('✅ Service states updated');
      setShowServiceStatesModal(false);
      setServiceStatesReason('');
      onSuccess();
    } catch (e: any) {
      alert(`❌ Error: ${e.message || 'Failed to update service states'}`);
    } finally {
      setLoading(false);
    }
  };

  return (
    <>
      <div style={{
        padding: '16px',
        background: '#fef3c7',
        borderRadius: '8px',
        marginBottom: '16px',
        border: '1px solid #fbbf24'
      }}>
        <h3 style={{ margin: '0 0 12px 0', fontSize: '16px', fontWeight: '600', color: '#92400e' }}>
          Direct Profile Editing
        </h3>
        <p style={{ margin: '0 0 12px 0', fontSize: '13px', color: '#78350f' }}>
          ⚠️ Bypasses verification flow - use for corrections and admin overrides
        </p>
        <div style={{ display: 'flex', gap: '8px', flexWrap: 'wrap' }}>
          <button
            onClick={() => setShowProfileModal(true)}
            disabled={loading}
            style={{
              padding: '8px 16px',
              background: '#3b82f6',
              color: '#fff',
              border: 'none',
              borderRadius: '6px',
              cursor: loading ? 'not-allowed' : 'pointer',
              fontSize: '14px',
              fontWeight: '500'
            }}
          >
            Edit Company Name
          </button>

          <button
            onClick={() => setShowLicenseModal(true)}
            disabled={loading}
            style={{
              padding: '8px 16px',
              background: '#8b5cf6',
              color: '#fff',
              border: 'none',
              borderRadius: '6px',
              cursor: loading ? 'not-allowed' : 'pointer',
              fontSize: '14px',
              fontWeight: '500'
            }}
          >
            Edit License Info
          </button>

          <button
            onClick={() => setShowInsuranceModal(true)}
            disabled={loading}
            style={{
              padding: '8px 16px',
              background: '#10b981',
              color: '#fff',
              border: 'none',
              borderRadius: '6px',
              cursor: loading ? 'not-allowed' : 'pointer',
              fontSize: '14px',
              fontWeight: '500'
            }}
          >
            Edit Insurance
          </button>

          <button
            onClick={() => setShowServiceStatesModal(true)}
            disabled={loading}
            style={{
              padding: '8px 16px',
              background: '#f59e0b',
              color: '#fff',
              border: 'none',
              borderRadius: '6px',
              cursor: loading ? 'not-allowed' : 'pointer',
              fontSize: '14px',
              fontWeight: '500'
            }}
          >
            Edit Service States
          </button>
        </div>
      </div>

      {/* Profile Edit Modal */}
      {showProfileModal && (
        <div
          style={{
            position: 'fixed',
            inset: 0,
            background: 'rgba(0,0,0,0.5)',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            zIndex: 1000
          }}
          onClick={() => setShowProfileModal(false)}
        >
          <div
            style={{
              background: '#fff',
              padding: '24px',
              borderRadius: '8px',
              maxWidth: '500px',
              width: '90%'
            }}
            onClick={(e) => e.stopPropagation()}
          >
            <h3 style={{ margin: '0 0 16px 0', fontSize: '18px', fontWeight: '600' }}>
              Edit Company Name
            </h3>

            <div style={{ marginBottom: '16px' }}>
              <label style={{ display: 'block', marginBottom: '4px', fontSize: '14px', fontWeight: '500' }}>
                Company Name
              </label>
              <input
                type="text"
                value={company}
                onChange={(e) => setCompany(e.target.value)}
                placeholder="Company name"
                style={{
                  width: '100%',
                  padding: '8px',
                  border: '1px solid #d1d5db',
                  borderRadius: '6px',
                  fontSize: '14px'
                }}
              />
            </div>

            <div style={{ marginBottom: '16px' }}>
              <label style={{ display: 'block', marginBottom: '4px', fontSize: '14px', fontWeight: '500' }}>
                Edit Reason (Required)
              </label>
              <textarea
                value={profileReason}
                onChange={(e) => setProfileReason(e.target.value)}
                placeholder="Why are you editing this profile?"
                rows={3}
                style={{
                  width: '100%',
                  padding: '8px',
                  border: '1px solid #d1d5db',
                  borderRadius: '6px',
                  fontSize: '14px',
                  resize: 'vertical'
                }}
              />
            </div>

            <div style={{ display: 'flex', gap: '8px', justifyContent: 'flex-end' }}>
              <button
                onClick={() => setShowProfileModal(false)}
                style={{
                  padding: '8px 16px',
                  background: '#fff',
                  color: '#374151',
                  border: '1px solid #d1d5db',
                  borderRadius: '6px',
                  cursor: 'pointer',
                  fontSize: '14px'
                }}
              >
                Cancel
              </button>
              <button
                onClick={handleProfileEdit}
                disabled={loading || !company.trim() || !profileReason.trim()}
                style={{
                  padding: '8px 16px',
                  background: loading || !company.trim() || !profileReason.trim() ? '#9ca3af' : '#3b82f6',
                  color: '#fff',
                  border: 'none',
                  borderRadius: '6px',
                  cursor: loading || !company.trim() || !profileReason.trim() ? 'not-allowed' : 'pointer',
                  fontSize: '14px'
                }}
              >
                {loading ? 'Saving...' : 'Save Changes'}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* License Edit Modal */}
      {showLicenseModal && (
        <div
          style={{
            position: 'fixed',
            inset: 0,
            background: 'rgba(0,0,0,0.5)',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            zIndex: 1000
          }}
          onClick={() => setShowLicenseModal(false)}
        >
          <div
            style={{
              background: '#fff',
              padding: '24px',
              borderRadius: '8px',
              maxWidth: '500px',
              width: '90%'
            }}
            onClick={(e) => e.stopPropagation()}
          >
            <h3 style={{ margin: '0 0 16px 0', fontSize: '18px', fontWeight: '600' }}>
              Edit License Information
            </h3>

            <div style={{ marginBottom: '16px' }}>
              <label style={{ display: 'block', marginBottom: '4px', fontSize: '14px', fontWeight: '500' }}>
                License Number
              </label>
              <input
                type="text"
                value={licenseNumber}
                onChange={(e) => setLicenseNumber(e.target.value)}
                placeholder="License number"
                style={{
                  width: '100%',
                  padding: '8px',
                  border: '1px solid #d1d5db',
                  borderRadius: '6px',
                  fontSize: '14px'
                }}
              />
            </div>

            <div style={{ marginBottom: '16px' }}>
              <label style={{ display: 'block', marginBottom: '4px', fontSize: '14px', fontWeight: '500' }}>
                License State
              </label>
              <input
                type="text"
                value={licenseState}
                onChange={(e) => setLicenseState(e.target.value)}
                placeholder="e.g., CA, TX, NY"
                maxLength={2}
                style={{
                  width: '100%',
                  padding: '8px',
                  border: '1px solid #d1d5db',
                  borderRadius: '6px',
                  fontSize: '14px'
                }}
              />
            </div>

            <div style={{ marginBottom: '16px' }}>
              <label style={{ display: 'block', marginBottom: '4px', fontSize: '14px', fontWeight: '500' }}>
                License Expiration Date
              </label>
              <input
                type="date"
                value={licenseExpiresAt}
                onChange={(e) => setLicenseExpiresAt(e.target.value)}
                style={{
                  width: '100%',
                  padding: '8px',
                  border: '1px solid #d1d5db',
                  borderRadius: '6px',
                  fontSize: '14px'
                }}
              />
            </div>

            <div style={{ marginBottom: '16px' }}>
              <label style={{ display: 'block', marginBottom: '4px', fontSize: '14px', fontWeight: '500' }}>
                Edit Reason (Required)
              </label>
              <textarea
                value={licenseReason}
                onChange={(e) => setLicenseReason(e.target.value)}
                placeholder="Why are you editing this license information?"
                rows={3}
                style={{
                  width: '100%',
                  padding: '8px',
                  border: '1px solid #d1d5db',
                  borderRadius: '6px',
                  fontSize: '14px',
                  resize: 'vertical'
                }}
              />
            </div>

            <div style={{ display: 'flex', gap: '8px', justifyContent: 'flex-end' }}>
              <button
                onClick={() => setShowLicenseModal(false)}
                style={{
                  padding: '8px 16px',
                  background: '#fff',
                  color: '374151',
                  border: '1px solid #d1d5db',
                  borderRadius: '6px',
                  cursor: 'pointer',
                  fontSize: '14px'
                }}
              >
                Cancel
              </button>
              <button
                onClick={handleLicenseEdit}
                disabled={loading || !licenseReason.trim()}
                style={{
                  padding: '8px 16px',
                  background: loading || !licenseReason.trim() ? '#9ca3af' : '#8b5cf6',
                  color: '#fff',
                  border: 'none',
                  borderRadius: '6px',
                  cursor: loading || !licenseReason.trim() ? 'not-allowed' : 'pointer',
                  fontSize: '14px'
                }}
              >
                {loading ? 'Saving...' : 'Save Changes'}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Insurance Edit Modal */}
      {showInsuranceModal && (
        <div
          style={{
            position: 'fixed',
            inset: 0,
            background: 'rgba(0,0,0,0.5)',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            zIndex: 1000
          }}
          onClick={() => setShowInsuranceModal(false)}
        >
          <div
            style={{
              background: '#fff',
              padding: '24px',
              borderRadius: '8px',
              maxWidth: '500px',
              width: '90%',
              maxHeight: '80vh',
              overflow: 'auto'
            }}
            onClick={(e) => e.stopPropagation()}
          >
            <h3 style={{ margin: '0 0 16px 0', fontSize: '18px', fontWeight: '600' }}>
              Edit Insurance Information
            </h3>

            <div style={{ marginBottom: '16px' }}>
              <label style={{ display: 'block', marginBottom: '4px', fontSize: '14px', fontWeight: '500' }}>
                General Liability Expiration
              </label>
              <input
                type="date"
                value={generalLiabilityExpiresAt}
                onChange={(e) => setGeneralLiabilityExpiresAt(e.target.value)}
                style={{
                  width: '100%',
                  padding: '8px',
                  border: '1px solid #d1d5db',
                  borderRadius: '6px',
                  fontSize: '14px'
                }}
              />
            </div>

            <div style={{ marginBottom: '16px' }}>
              <label style={{ display: 'block', marginBottom: '4px', fontSize: '14px', fontWeight: '500' }}>
                Workers Comp Expiration
              </label>
              <input
                type="date"
                value={workersCompExpiresAt}
                onChange={(e) => setWorkersCompExpiresAt(e.target.value)}
                style={{
                  width: '100%',
                  padding: '8px',
                  border: '1px solid #d1d5db',
                  borderRadius: '6px',
                  fontSize: '14px'
                }}
              />
            </div>

            <div style={{ marginBottom: '16px' }}>
              <label style={{ display: 'flex', alignItems: 'center', fontSize: '14px', fontWeight: '500', cursor: 'pointer' }}>
                <input
                  type="checkbox"
                  checked={workersCompRequired}
                  onChange={(e) => setWorkersCompRequired(e.target.checked)}
                  style={{ marginRight: '8px' }}
                />
                Workers Comp Required
              </label>
            </div>

            <div style={{ marginBottom: '16px' }}>
              <label style={{ display: 'block', marginBottom: '4px', fontSize: '14px', fontWeight: '500' }}>
                Edit Reason (Required)
              </label>
              <textarea
                value={insuranceReason}
                onChange={(e) => setInsuranceReason(e.target.value)}
                placeholder="Why are you editing this insurance information?"
                rows={3}
                style={{
                  width: '100%',
                  padding: '8px',
                  border: '1px solid #d1d5db',
                  borderRadius: '6px',
                  fontSize: '14px',
                  resize: 'vertical'
                }}
              />
            </div>

            <div style={{ display: 'flex', gap: '8px', justifyContent: 'flex-end' }}>
              <button
                onClick={() => setShowInsuranceModal(false)}
                style={{
                  padding: '8px 16px',
                  background: '#fff',
                  color: '#374151',
                  border: '1px solid #d1d5db',
                  borderRadius: '6px',
                  cursor: 'pointer',
                  fontSize: '14px'
                }}
              >
                Cancel
              </button>
              <button
                onClick={handleInsuranceEdit}
                disabled={loading || !insuranceReason.trim()}
                style={{
                  padding: '8px 16px',
                  background: loading || !insuranceReason.trim() ? '#9ca3af' : '#10b981',
                  color: '#fff',
                  border: 'none',
                  borderRadius: '6px',
                  cursor: loading || !insuranceReason.trim() ? 'not-allowed' : 'pointer',
                  fontSize: '14px'
                }}
              >
                {loading ? 'Saving...' : 'Save Changes'}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Service States Edit Modal */}
      {showServiceStatesModal && (
        <div
          style={{
            position: 'fixed',
            inset: 0,
            background: 'rgba(0,0,0,0.5)',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            zIndex: 1000
          }}
          onClick={() => setShowServiceStatesModal(false)}
        >
          <div
            style={{
              background: '#fff',
              padding: '24px',
              borderRadius: '8px',
              maxWidth: '500px',
              width: '90%'
            }}
            onClick={(e) => e.stopPropagation()}
          >
            <h3 style={{ margin: '0 0 16px 0', fontSize: '18px', fontWeight: '600' }}>
              Edit Service States
            </h3>

            <div style={{ marginBottom: '16px' }}>
              <label style={{ display: 'block', marginBottom: '4px', fontSize: '14px', fontWeight: '500' }}>
                Service States (comma-separated)
              </label>
              <input
                type="text"
                value={serviceStatesText}
                onChange={(e) => setServiceStatesText(e.target.value)}
                placeholder="e.g., CA, TX, NY, FL"
                style={{
                  width: '100%',
                  padding: '8px',
                  border: '1px solid #d1d5db',
                  borderRadius: '6px',
                  fontSize: '14px'
                }}
              />
              <p style={{ margin: '4px 0 0 0', fontSize: '12px', color: '#6b7280' }}>
                Enter state abbreviations separated by commas
              </p>
            </div>

            <div style={{ marginBottom: '16px' }}>
              <label style={{ display: 'block', marginBottom: '4px', fontSize: '14px', fontWeight: '500' }}>
                Edit Reason (Required)
              </label>
              <textarea
                value={serviceStatesReason}
                onChange={(e) => setServiceStatesReason(e.target.value)}
                placeholder="Why are you editing service states?"
                rows={3}
                style={{
                  width: '100%',
                  padding: '8px',
                  border: '1px solid #d1d5db',
                  borderRadius: '6px',
                  fontSize: '14px',
                  resize: 'vertical'
                }}
              />
            </div>

            <div style={{ display: 'flex', gap: '8px', justifyContent: 'flex-end' }}>
              <button
                onClick={() => setShowServiceStatesModal(false)}
                style={{
                  padding: '8px 16px',
                  background: '#fff',
                  color: '#374151',
                  border: '1px solid #d1d5db',
                  borderRadius: '6px',
                  cursor: 'pointer',
                  fontSize: '14px'
                }}
              >
                Cancel
              </button>
              <button
                onClick={handleServiceStatesEdit}
                disabled={loading || !serviceStatesReason.trim()}
                style={{
                  padding: '8px 16px',
                  background: loading || !serviceStatesReason.trim() ? '#9ca3af' : '#f59e0b',
                  color: '#fff',
                  border: 'none',
                  borderRadius: '6px',
                  cursor: loading || !serviceStatesReason.trim() ? 'not-allowed' : 'pointer',
                  fontSize: '14px'
                }}
              >
                {loading ? 'Saving...' : 'Save Changes'}
              </button>
            </div>
          </div>
        </div>
      )}
    </>
  );
};
// CB:END admin-priority2-contractor-profile-editor-component
